import { DataType, FilterButton as FilterButtonType } from "@/mixins/@types/map-filter";
import { fetchPlaceCategoryList } from "@/api/adapter.api";
import { PortalAdapterApi } from "@vacancorp/portal.adapter.api";
import $i18n from "@/i18n";
import { getAreaListForRereshUI, findSubarea } from "@/usecase/language";
import OpendataModuleMixin from "@/mixins/opendata-module.mixin";
import TypesMixin from "vue-typed-mixins";

export default TypesMixin(OpendataModuleMixin).extend({
  name: "mixin-map-filter-buttons",
  mixins: [OpendataModuleMixin],
  data(): DataType {
    return {
      filterButtons: [],
    };
  },
  computed: {
    language(): string {
      return this.$store.state.data.language;
    },
    selectedGenre(): PortalAdapterApi.PlaceCategoryType | undefined {
      return this.$store.state.data.selectedGenre;
    },
    genreList(): PortalAdapterApi.ResponseGetPlaceCategoryList {
      return this.$store.state.data.genreList;
    },
    selectedFilterRadioButtons(): string[] {
      return this.$store.state.data.selectedFilterRadioButtons;
    },
    isNotNormalMap(): boolean {
      return this.$store.getters["data/isNotNormalMap"];
    },
    /** @remarks displayOrder
     * 1: head, 2: tune menu, 3: area
     * 4: genre, 5: congestion, 7: favorite
     */
    displayOrderFilterButtons(): FilterButtonType[] {
      console.log(`displayOrderFilterButtons`);
      let sorted: FilterButtonType[] = this.filterButtons.sort((a, b) =>
        a.displayOrder ? Number(b.selected) - Number(a.selected) : -1,
      );

      // fixed menu, tune
      let headElements: FilterButtonType[] = sorted.filter((btn) => btn.displayOrder < 3);

      // selected = false
      let sortNonselected: FilterButtonType[] = sorted.filter((btn) => btn.selected === false && btn.displayOrder > 2);
      sortNonselected = sortNonselected.sort((a, b) => a.displayOrder - b.displayOrder);

      // selected = true
      let sortSelected: FilterButtonType[] = sorted.filter((btn) => btn.selected === true && btn.displayOrder > 2);
      sortSelected = sortSelected.sort((a, b) => a.displayOrder - b.displayOrder);

      const result: FilterButtonType[] = headElements.concat(sortSelected.concat(sortNonselected));
      return result;
    },
    /**
     * @remarks mobile design is different from PC
     * so the functions here should be seperated
     */
    displayOrderFilterButtonsForMobile(): FilterButtonType[] {
      const allButtons = [...this.displayOrderFilterButtons];
      allButtons.splice(0, 2);
      return allButtons;
    },
    // Menu and Tune btn are fixed on mobile design
    displayMenuTuneButtonsForMobile(): FilterButtonType[] {
      const allButtons = [...this.displayOrderFilterButtons];
      allButtons.splice(2, allButtons.length - 2);
      return allButtons;
    },
  },
  created() {
    this.initFilterButtons();
    this.watchLanguageChanged();
  },
  methods: {
    watchLanguageChanged() {
      this.$watch(
        () => this.language,
        async () => {
          const countryName: string | undefined = this.$store.getters["data/getSelectedCountry"];
          const mapType =
            !this.selectedGenre ||
            !["evacuation-center", "polling-place", "disaster-prevention"].includes(this.selectedGenre)
              ? "normal"
              : this.selectedGenre;

          const areaList: PortalAdapterApi.ResponseGetTopAreaList = await getAreaListForRereshUI(countryName, mapType);
          const subarea: PortalAdapterApi.TopArea | undefined = findSubarea(areaList);

          // load genre again
          const genreList: PortalAdapterApi.ResponseGetPlaceCategoryList = await this.getPlaceCategoryList(
            countryName,
          ).catch(() => {
            console.error(`get genre error`);
            return [];
          });
          const targetGenre: undefined | PortalAdapterApi.PlaceCategory = genreList.find(
            (genre: PortalAdapterApi.PlaceCategory) => genre.placeCategoryType === this.selectedGenre,
          );

          // re-init button title again
          const filterTitle = this.$i18n.t("Filter.title").toString();
          const genreTitle =
            targetGenre === undefined ? this.$i18n.t("Nav.placeCateogory").toString() : targetGenre.displayText;
          const genreName = targetGenre?.placeCategoryType;
          const areaTitle = subarea === undefined ? this.$i18n.t("Nav.area").toString() : subarea.displayText;

          // update filter buttons
          this.setupFilterButtonsByLangChanged(filterTitle, areaTitle, genreTitle, genreName);
        },
      );
    },
    initFilterButtons(): void {
      this.filterButtons = [
        {
          name: "menu",
          iconSrc: require("@/assets/images/head.svg"),
          displayOrder: 1,
          selected: false,
        },
        {
          name: "tune",
          title: this.$i18n.t("Filter.title") as string,
          iconName: "tune",
          displayOrder: 2,
          selected: false,
        },
        {
          name: "area",
          title: this.$i18n.t("Nav.area") as string,
          displayOrder: 3,
          selected: false,
        },
        {
          name: "genre",
          title: this.$i18n.t("Nav.placeCateogory") as string,
          displayOrder: 4,
          selected: false,
        },
        {
          name: "vacant",
          iconName: `congestion`,
          title: this.$i18n.t("Filter.vacant") as string,
          displayOrder: 5,
          selected: false,
        },
        {
          name: "favorite",
          iconName: "favorite",
          title: this.$i18n.t("MapLeftPanel.favorite") as string,
          displayOrder: 7,
          selected: false,
        },
      ];
    },
    setupFilterButtonsByLangChanged(
      filterTitle: string,
      areaTitle: string,
      genreTitle: string,
      genreName?: string,
    ): void {
      let newButtons: FilterButtonType[] = [...this.filterButtons];
      newButtons.forEach((filterBtn) => {
        if (filterBtn.name === "tune") filterBtn.title = filterTitle;
        if (filterBtn.name === "area") filterBtn.title = areaTitle;
        if (filterBtn.name === "genre") {
          (filterBtn.title = genreTitle), (filterBtn.iconName = genreName === undefined ? undefined : genreName);
        }
        if (filterBtn.name === "vacant") filterBtn.title = $i18n.t("Filter.vacant") as string;

        if (filterBtn.name === "favorite") filterBtn.title = $i18n.t("MapLeftPanel.favorite") as string;
      });
      this.filterButtons = newButtons;
    },
    setFilterButtonSelected(btnName: string, status: boolean): void {
      this.filterButtons.forEach((filterBtn) => {
        if (filterBtn.name === btnName) filterBtn.selected = status;
      });
    },
    selectAreaFilterButtonMixin(_topPlace: PortalAdapterApi.TopArea) {
      this.setFilterButtonSelected(
        "area",
        _topPlace !== undefined &&
          _topPlace.areaName !== "Default" &&
          _topPlace.areaName !== "current-place" &&
          _topPlace.areaName !== ""
          ? true
          : false,
      );
    },
    async getPlaceCategoryList(
      countryName: string | undefined,
    ): Promise<PortalAdapterApi.ResponseGetPlaceCategoryList> {
      if (countryName === undefined) return [];
      return await fetchPlaceCategoryList(countryName).catch(() => {
        console.error(`get genre error`);
        return [];
      });
    },
    // update UI on selectedGenre, selectTopPlace changed
    setupGenreFilterButton(filterButtons: FilterButtonType[], pGenre: string | undefined) {
      if (pGenre === undefined) {
        filterButtons.forEach((btn: FilterButtonType) => {
          if (btn.name === "genre") {
            btn.iconName = undefined;
            btn.iconSrc = undefined;
            btn.title = this.$i18n.t("Nav.placeCateogory") as string;
            btn.selected = false;
          }
        });
        return;
      }

      const target: PortalAdapterApi.PlaceCategory | undefined =
        this.genreList === undefined
          ? undefined
          : this.genreList.find((genre: PortalAdapterApi.PlaceCategory) => genre.placeCategoryType === pGenre);
      if (pGenre !== "all" && target === undefined) return;

      filterButtons.forEach((btn: FilterButtonType) => {
        if (btn.name === "genre") {
          if (pGenre === "all") {
            btn.iconName = undefined;
            btn.iconSrc = undefined;
            btn.title = this.$i18n.t("Filter.normalGenre") as string;
            btn.selected = false;
          } else {
            btn.iconName = pGenre;
            btn.title = target?.displayText;
            btn.selected = true;
          }
        }
      });
    },
    assignAreaButton(place?: PortalAdapterApi.TopArea) {
      const targetBtn = this.filterButtons.find((button) => button.name === "area");
      if (
        targetBtn === undefined ||
        place?.areaName === "" ||
        place?.areaName === "Default" ||
        place?.areaName === "current-place"
      )
        return;
      targetBtn.title = place?.displayText;
      targetBtn.selected = true;
    },
    // hide on genre evacuation and poll
    hideFilterButton(item: FilterButtonType): boolean {
      if (this.isNotNormalMap) {
        // 常にジャンルを隠す
        if (item.name === "genre") return true;
      }
      if (this.selectedGenre === "disaster-prevention") {
        if (["area", "vacant", "favorite"].includes(item.name)) return true;
      }
      return false;
    },
  },
});
